export interface ReadoutRow {
  id: string;
  lhs: string;
  relation: '=' | '≈' | '<' | '>' | '≤' | '≥' | '∥' | '⊥';
  rhs: string;
  emphasis?: 'invariant' | 'broken' | 'neutral';
}

export interface ReadoutHandle {
  update(rows: readonly ReadoutRow[]): void;
  destroy(): void;
}

export function createReadout(container: HTMLElement): ReadoutHandle {
  const rowMap = new Map<string, HTMLElement>();

  const handle: ReadoutHandle = {
    update(rows: readonly ReadoutRow[]): void {
      const currentIds = new Set(rowMap.keys());
      const newIds = new Set(rows.map((row) => row.id));

      // Remove rows no longer in the array
      for (const id of currentIds) {
        if (!newIds.has(id)) {
          const el = rowMap.get(id);
          if (el?.parentNode === container) {
            container.removeChild(el);
          }
          rowMap.delete(id);
        }
      }

      // Update or create rows in order
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        let rowEl = rowMap.get(row.id);

        if (!rowEl) {
          // Create new row element
          rowEl = document.createElement('div');
          rowEl.setAttribute('data-role', 'readout-row');

          const lhsSpan = document.createElement('span');
          lhsSpan.setAttribute('data-part', 'lhs');
          rowEl.appendChild(lhsSpan);

          const relationSpan = document.createElement('span');
          relationSpan.setAttribute('data-part', 'relation');
          rowEl.appendChild(relationSpan);

          const rhsSpan = document.createElement('span');
          rhsSpan.setAttribute('data-part', 'rhs');
          rowEl.appendChild(rhsSpan);

          rowMap.set(row.id, rowEl);
          container.appendChild(rowEl);
        }

        // Update text content
        const lhsSpan = rowEl.querySelector('[data-part="lhs"]') as HTMLElement;
        const relationSpan = rowEl.querySelector('[data-part="relation"]') as HTMLElement;
        const rhsSpan = rowEl.querySelector('[data-part="rhs"]') as HTMLElement;

        lhsSpan.textContent = row.lhs;
        relationSpan.textContent = row.relation;
        rhsSpan.textContent = row.rhs;

        // Update emphasis class
        rowEl.classList.remove('geo-readout-row--invariant', 'geo-readout-row--broken');

        if (row.emphasis === 'invariant') {
          rowEl.classList.add('geo-readout-row--invariant');
        } else if (row.emphasis === 'broken') {
          rowEl.classList.add('geo-readout-row--broken');
        }

        // Ensure correct DOM position
        const currentIndex = Array.from(container.children).indexOf(rowEl);
        if (currentIndex !== i) {
          // Re-insert at correct position
          if (i === 0) {
            container.insertBefore(rowEl, container.firstChild);
          } else {
            const prevRow = Array.from(rowMap.values()).filter((el) => el.parentNode === container)[
              i - 1
            ];
            if (prevRow?.nextSibling) {
              container.insertBefore(rowEl, prevRow.nextSibling);
            } else if (prevRow) {
              container.insertBefore(rowEl, prevRow.nextSibling);
            }
          }
        }
      }
    },

    destroy(): void {
      container.innerHTML = '';
      rowMap.clear();
    },
  };

  return handle;
}
